import { useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { getBaseUrlWithoutApi } from '../config/api';

export type TypingState = {
  userId: string;
  userName?: string;
};

export const useConversationTypingSocket = (
  token: string | null,
  userId: string | undefined,
  conversationIds: string[],
  onConversationUpdated?: (data: {
    conversationId: string;
    lastMessage: {
      content: string;
      sender: string;
      createdAt: string;
    };
  }) => void
) => {
  const [typingByConversation, setTypingByConversation] = useState<Record<string, TypingState>>({});
  const socketRef = useRef<Socket | null>(null);
  const typingTimeoutsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const conversationKey = conversationIds.join(',');

  useEffect(() => {
    if (!token || !userId || conversationKey.length === 0) {
      return;
    }

    const ids = conversationKey.split(',').filter(Boolean);
    if (ids.length === 0) {
      return;
    }

    const socket = io(getBaseUrlWithoutApi(), {
      transports: ['websocket'],
      auth: { token },
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: Infinity,
      timeout: 20000,
    });

    socketRef.current = socket;

    const joinRooms = () => {
      ids.forEach((conversationId) => {
        socket.emit('joinConversation', conversationId);
      });
    };

    const clearTypingTimeout = (conversationId: string) => {
      const timer = typingTimeoutsRef.current.get(conversationId);
      if (timer) {
        clearTimeout(timer);
        typingTimeoutsRef.current.delete(conversationId);
      }
    };

    const setConversationTyping = (conversationId: string, typing: TypingState | null) => {
      setTypingByConversation((prev) => {
        if (!typing) {
          if (!prev[conversationId]) return prev;
          const next = { ...prev };
          delete next[conversationId];
          return next;
        }
        return { ...prev, [conversationId]: typing };
      });
    };

    socket.on('connect', joinRooms);
    socket.on('reconnect', joinRooms);

    socket.on('userTyping', (data: { conversationId?: string; userId?: string; userName?: string }) => {
      if (!data?.conversationId || !data.userId || data.userId === userId) {
        return;
      }
      setConversationTyping(data.conversationId, {
        userId: data.userId,
        userName: data.userName,
      });
      clearTypingTimeout(data.conversationId);
      typingTimeoutsRef.current.set(
        data.conversationId,
        setTimeout(() => {
          setConversationTyping(data.conversationId!, null);
          typingTimeoutsRef.current.delete(data.conversationId!);
        }, 4000)
      );
    });

    socket.on('userStoppedTyping', (data: { conversationId?: string; userId?: string }) => {
      if (!data?.conversationId || !data.userId || data.userId === userId) {
        return;
      }
      clearTypingTimeout(data.conversationId);
      setConversationTyping(data.conversationId, null);
    });

    socket.on(
      'conversationUpdated',
      (data: {
        conversationId: string;
        lastMessage: { content: string; sender: string; createdAt: string };
      }) => {
        onConversationUpdated?.(data);
      }
    );

    const timeouts = typingTimeoutsRef.current;

    return () => {
      ids.forEach((conversationId) => {
        socket.emit('leaveConversation', conversationId);
      });
      timeouts.forEach((timer) => clearTimeout(timer));
      timeouts.clear();
      socket.off('connect');
      socket.off('reconnect');
      socket.off('userTyping');
      socket.off('userStoppedTyping');
      socket.off('conversationUpdated');
      socket.disconnect();
      socketRef.current = null;
      setTypingByConversation({});
    };
  }, [token, userId, conversationKey, onConversationUpdated]);

  return typingByConversation;
};
